/**
 * One acceptance criterion (layers 1 and 2): its one state, why it is not proved when
 * it is not, and how it was checked.
 *
 * @module features/proofs/components/CriterionCard
 */
import React from 'react';
import { CollapsibleSection, Text } from '@chatsdet/ui';
import { criterionReason } from '../evidenceKinds';
import type { ProofCriterion, ProofEvidence } from '../types';
import { CheckTypeChips } from './CheckTypeChips';
import { EvidenceGroups } from './EvidenceGroups';
import { ItemStatusBadge } from './ItemStatusBadge';
import { OutcomeBadge } from './OutcomeBadge';

type Props = { criterion: ProofCriterion; evidence: ProofEvidence[]; defaultExpanded: boolean };

export const CriterionCard: React.FC<Props> = ({ criterion, evidence, defaultExpanded }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (defaultExpanded) ref.current?.scrollIntoView({ block: 'start' });
  }, [defaultExpanded]);

  const reason = criterionReason(criterion);
  return (
    <div ref={ref} id={`criterion-${criterion.position}`} className="scroll-mt-4 px-4 py-1">
      <CollapsibleSection
        hideBorder
        defaultExpanded={defaultExpanded}
        label={`Criterion ${criterion.position}`}
        header={
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Text weight="semibold">{`${criterion.position}. ${criterion.text}`}</Text>
            <OutcomeBadge state={criterion.state} />
            {criterion.status.kind === 'stale' && <ItemStatusBadge status={criterion.status} />}
            {criterion.state !== 'checked-another-way' && <CheckTypeChips evidence={evidence} />}
          </div>
        }
        bodyClassName="space-y-2 pl-6"
      >
        {reason && <Text data-testid="criterion-reason">{reason}</Text>}
        {evidence.length > 0 && (
          <>
            <Text weight="semibold">How it was checked</Text>
            <EvidenceGroups evidence={evidence} forceExpanded={defaultExpanded} />
          </>
        )}
      </CollapsibleSection>
    </div>
  );
};
